import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

type TabFinancieroProps = {
  clienteData: any;
};

type ResumenItemProps = {
  label: string;
  value: string | number | null | undefined;
  color?: string;
};

function ResumenItem({ label, value, color }: ResumenItemProps) {
  return (
    <Grid item xs={6} sm={3}>
      <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight="bold" sx={{ color: color || 'text.primary' }}>
          {value ?? '-'}
        </Typography>
      </Card>
    </Grid>
  );
}

export default function TabFinanciero({ clienteData }: TabFinancieroProps) {
  const financiero = clienteData?.financiero || {};
  const facturas: any[] = financiero.facturas_recientes ?? [];
  const totalPendiente = financiero.total_pendiente ?? '0';
  const cantidadFacturas = financiero.cantidad_facturas ?? 0;
  const pagos: any[] = Array.isArray(financiero.pagos) ? financiero.pagos : [];
  const deudas: any[] = Array.isArray(financiero.deudas) ? financiero.deudas : [];
  const saldo = financiero.saldo;
  const fibraPoints = financiero.fibra_points;
  const historialPoints: any[] = Array.isArray(fibraPoints?.historial) ? fibraPoints.historial : [];
  const serviciosAdicionales: Record<string, any> =
    typeof financiero.servicios_adicionales === 'object' && financiero.servicios_adicionales !== null
      ? financiero.servicios_adicionales
      : {};

  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Resumen Financiero
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <ResumenItem label="Facturas" value={cantidadFacturas} />
            <ResumenItem
              label="Total Pendiente"
              value={`$${totalPendiente}`}
              color={parseFloat(totalPendiente) > 0 ? '#d32f2f' : '#4caf50'}
            />
            {saldo !== undefined && (
              <ResumenItem
                label="Saldo (Softnet)"
                value={`$${saldo}`}
                color={parseFloat(`${saldo}`) > 0 ? '#d32f2f' : '#4caf50'}
              />
            )}
            {fibraPoints?.puntos_disponibles !== undefined && (
              <ResumenItem
                label="Fibra Points"
                value={fibraPoints.puntos_disponibles}
                color="#1976d2"
              />
            )}
          </Grid>

          {/* Facturas */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Facturas Recientes
          </Typography>
          {facturas.length === 0 ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>Sin facturas recientes.</Alert>
          ) : (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Fecha Emision</TableCell>
                    <TableCell>Secuencial</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((f: any, index: number) => (
                    <TableRow key={f.codigo ?? index}>
                      <TableCell>{f.codigo ?? '-'}</TableCell>
                      <TableCell>{f.fecha_emision ?? '-'}</TableCell>
                      <TableCell>{f.secuencial ?? '-'}</TableCell>
                      <TableCell align="right">{f.total != null ? `$${f.total}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagos (Softnet) */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Pagos ({pagos.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {pagos.length === 0 ? (
            <Alert severity="info" variant="outlined">Sin pagos registrados.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Período</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">A pagar</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell>Documento</TableCell>
                    <TableCell>Fecha pago</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagos.map((p: any, idx: number) => (
                    <TableRow key={p.idValorGenerado || idx}>
                      <TableCell>{p.idMes && p.anio ? `${p.idMes}/${p.anio}` : '-'}</TableCell>
                      <TableCell>{p.observaciones || '-'}</TableCell>
                      <TableCell align="right">${p.totalAPagar ?? '-'}</TableCell>
                      <TableCell align="right">${p.totalPagado ?? '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.numeroDocumento || '-'}</TableCell>
                      <TableCell>{p.fechaPago || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Deudas */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Deudas ({deudas.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {deudas.length === 0 ? (
            <Alert severity="success" variant="outlined">El cliente no tiene deudas pendientes.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Período</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Vencimiento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deudas.map((d: any, idx: number) => (
                    <TableRow key={d.idValorGenerado || idx}>
                      <TableCell>{d.idMes && d.anio ? `${d.idMes}/${d.anio}` : '-'}</TableCell>
                      <TableCell>{d.observaciones || '-'}</TableCell>
                      <TableCell align="right">${d.totalAPagar ?? '-'}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                        ${d.valorSaldo ?? '-'}
                      </TableCell>
                      <TableCell>{d.fechaVencimiento || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Fibra Points - historial */}
      {fibraPoints && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Fibra Points - Historial ({historialPoints.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {historialPoints.length === 0 ? (
              <Alert severity="info" variant="outlined">Sin canjes registrados.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Causal</TableCell>
                      <TableCell>Ticket</TableCell>
                      <TableCell align="right">Canjeados</TableCell>
                      <TableCell align="right">Antes</TableCell>
                      <TableCell align="right">Después</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historialPoints.map((h: any, idx: number) => (
                      <TableRow key={h.id ?? idx}>
                        <TableCell>{h.nombre_causal || '-'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{h.idTicket || '-'}</TableCell>
                        <TableCell align="right">{h.puntos_canjeados ?? '-'}</TableCell>
                        <TableCell align="right">{h.puntos_antes_canje ?? '-'}</TableCell>
                        <TableCell align="right">{h.puntos_despues_canje ?? '-'}</TableCell>
                        <TableCell align="right">${h.valor_real ?? '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={h.status || '-'}
                            size="small"
                            color={h.status === 'PROCESADO' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{h.created_at || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Servicios adicionales */}
      {Object.keys(serviciosAdicionales).length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Servicios adicionales
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Object.entries(serviciosAdicionales).map(([nombre, info]: [string, any]) => (
                <Grid item xs={12} sm={6} md={4} key={nombre}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        {nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {info?.tipo_plan || '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {facturas.length === 0 && pagos.length === 0 && deudas.length === 0 && historialPoints.length === 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No hay datos financieros disponibles.
          </Typography>
        </Box>
      )}
    </Grid>
  );
}
